import { AppDispatch } from '@/app/store';
import { Box, Container, Grid, useMediaQuery } from '@mui/material';
import { CookieConsentBanner } from '@/components/common/CookieConsentBanner';
import { ErrorDTO } from '@/types/ErrorDTO';
import { Footer } from '@/components/main/footer/Footer';
import { Location, NavigateFunction, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { MainNavigation } from '@/components/main/navigation/MainNavigation';
import { MobileHeader } from '@/components/common/MobileHeader';
import {
  ROUTE_REGISTRATION,
  ROUTE_REGISTRATION_CONFIRM_SUCCESS,
  ROUTE_RESTORE,
  ROUTE_RESTORE_SUCCESS,
} from '@/app/routes';
import { RegistrationBanner } from '@/components/auth/RegistrationBanner';
import { Theme } from '@mui/material/styles';
import { TopLine } from '@/components/common/TopLine';
import { UserDTO } from '@/types/user/UserDTO';
import { getAuthenticated, getCurrentUser, signOut } from '@/services/authSlice';
import { setNewEmail } from '@/services/profileSlice';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { useConfirmChangeEmailMutation, useConfirmRegistrationMutation } from '@/services/api/authApiSlice';
import { useConfirmEmailTokenMutation } from '@/services/api/settingsApiSlice';
import { useSnackbar } from 'notistack';
import React, { FC, Fragment, ReactElement, useEffect } from 'react';

export const AuthorizationPage: FC = (): ReactElement => {
  const dispatch: AppDispatch = useAppDispatch();
  const location: Location = useLocation();
  const navigate: NavigateFunction = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const isAuthenticated: boolean = useAppSelector(getAuthenticated);
  const authUser: UserDTO | undefined = useAppSelector(getCurrentUser);

  const [confirmEmail] = useConfirmEmailTokenMutation();
  const [confirmRegistration] = useConfirmRegistrationMutation();
  const [confirmChangeEmail] = useConfirmChangeEmailMutation();

  const isMobile: boolean = useMediaQuery((theme: Theme): string => theme.breakpoints.down('sm'));

  useEffect((): void => {
    const params: URLSearchParams = new URLSearchParams(location.search);
    const activateToken: string | null = params.get('activateToken');
    const confirmToken: string | null = params.get('confirmToken');
    const changeEmailToken: string | null = params.get('changeEmailToken');
    if (activateToken) {
      confirmEmail(activateToken)
        .unwrap()
        .then((): void => {
          dispatch(signOut());
          dispatch(setNewEmail(true));
          navigate(ROUTE_RESTORE_SUCCESS, { replace: true });
        });
    }
    if (confirmToken) {
      confirmRegistration(confirmToken)
        .unwrap()
        .then((): void => {
          navigate(ROUTE_REGISTRATION_CONFIRM_SUCCESS, { replace: true });
        })
        .catch((e: { status: number; data: ErrorDTO }) => {
          enqueueSnackbar(e.data?.message, { variant: 'error' });
        });
    }
    if (changeEmailToken) {
      confirmChangeEmail(changeEmailToken)
        .unwrap()
        .then((): void => {
          dispatch(signOut());
          dispatch(setNewEmail(true));
          navigate(ROUTE_REGISTRATION_CONFIRM_SUCCESS, { replace: true });
        })
        .catch((e: { status: number; data: ErrorDTO }) => {
          enqueueSnackbar(e.data?.message, { variant: 'error' });
        });
    }
  }, [confirmChangeEmail, confirmEmail, confirmRegistration, dispatch, enqueueSnackbar, location.search, navigate]);

  return (
    <Fragment>
      {!isMobile && <TopLine />}
      <Container
        sx={(theme: Theme) => ({
          display: 'flex',
          flexDirection: 'column',
          marginTop: '-6px',
          minHeight: '100%',
          [theme.breakpoints.down('sm')]: {
            marginTop: 0,
            paddingTop: theme.spacing(1),
            paddingBottom: theme.spacing(5),
            backgroundColor: theme.colors.grayBackground,
            height: '100%',
            overflow: 'auto',
          },
        })}>
        <Box display={'flex'} flex={1}>
          <Grid container={true} direction={'column'} wrap={'nowrap'} spacing={isMobile ? 4 : 12}>
            <Grid item={true}>
              {isMobile ? (
                <MobileHeader hideMenu={location.pathname === ROUTE_RESTORE} mainPage={true} />
              ) : (
                <MainNavigation profile={!!authUser} isAuthenticated={isAuthenticated} />
              )}
            </Grid>
            <Grid item={true} xs={true} display={'flex'} justifyContent={'center'}>
              <Grid container={true} direction={'column'} spacing={2}>
                {!isMobile && location.pathname === ROUTE_REGISTRATION && (
                  <Grid item={true} xs={true} display={'flex'} justifyContent={'center'} marginTop={-9}>
                    <RegistrationBanner />
                  </Grid>
                )}
                <Grid item={true} xs={true} display={'flex'} justifyContent={'center'}>
                  <Box
                    display={'flex'}
                    justifyContent={'center'}
                    position={'relative'}
                    width={'100%'}
                    height={'min-content'}
                    borderRadius={'12px'}
                    maxWidth={(theme: Theme) => theme.spacing(80)}
                    padding={(theme: Theme) => {
                      return isMobile ? theme.spacing(0) : theme.spacing(6, 5, 7);
                    }}
                    sx={{
                      backgroundColor: (theme: Theme) => theme.colors.grayBackground,
                    }}>
                    <Box display={'flex'} width={'100%'} maxWidth={(theme: Theme) => theme.spacing(40)}>
                      <Outlet />
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Grid>
            <Grid item={true}>
              <Footer isMobile={isMobile} />
            </Grid>
          </Grid>
        </Box>
        <CookieConsentBanner />
      </Container>
    </Fragment>
  );
};
